/**
 * Markdown renderer for the AI Console — GitHub-flavored markdown with
 * syntax-highlighted code/KQL blocks and a copy-to-clipboard button per block.
 */
import { memo, useMemo, useState, type ComponentProps, type ReactNode, type ReactElement } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { Check, Copy } from 'lucide-react';
import { cn } from '@respectly/ui';
import 'highlight.js/styles/github-dark.css';

function extractText(node: ReactNode): string {
  if (node == null || node === false) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  const el = node as ReactElement<{ children?: ReactNode }>;
  if (el?.props?.children !== undefined) return extractText(el.props.children);
  return '';
}

function CodeBlock({ children }: { children?: ReactNode }) {
  const [copied, setCopied] = useState(false);
  // Memoize the recursive tree walk so the copy-button toggle doesn't re-run it.
  const code = useMemo(() => extractText(children), [children]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="group relative my-3">
      <button
        type="button"
        onClick={copy}
        aria-label="Copy code"
        className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-md bg-zinc-800/80 px-2 py-1 text-[11px] font-medium text-zinc-200 opacity-0 transition-opacity hover:bg-zinc-700 group-hover:opacity-100"
      >
        {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      <pre className="overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-900 p-3 text-[12.5px] leading-relaxed text-zinc-100">
        {children}
      </pre>
    </div>
  );
}

export const Markdown = memo(function Markdown({
  content,
  className,
  streaming = false,
}: {
  content: string;
  className?: string;
  streaming?: boolean;
}) {
  // Syntax highlighting (highlight.js auto-detection over every registered language,
  // per code block) is the heaviest render-time cost. Skip it while streaming — code
  // shows as plain monospace — and apply it once, when the message completes.
  const rehypePlugins: ComponentProps<typeof ReactMarkdown>['rehypePlugins'] = streaming
    ? []
    : [[rehypeHighlight, { detect: true, ignoreMissing: true }]];
  return (
    <div
      className={cn(
        'text-sm leading-relaxed text-zinc-800 dark:text-zinc-200',
        '[&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
        '[&_li]:my-0.5 [&_h1]:mt-3 [&_h1]:mb-1.5 [&_h1]:text-base [&_h1]:font-semibold',
        '[&_h2]:mt-3 [&_h2]:mb-1.5 [&_h2]:text-[15px] [&_h2]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_h3]:font-semibold',
        '[&_a]:font-medium [&_a]:text-violet-600 [&_a]:underline dark:[&_a]:text-violet-400',
        '[&_:not(pre)>code]:rounded [&_:not(pre)>code]:bg-zinc-100 [&_:not(pre)>code]:px-1 [&_:not(pre)>code]:py-0.5 [&_:not(pre)>code]:text-[12.5px] dark:[&_:not(pre)>code]:bg-zinc-800',
        '[&_table]:my-2 [&_table]:w-full [&_table]:text-xs [&_th]:border [&_th]:border-zinc-200 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left dark:[&_th]:border-zinc-700',
        '[&_td]:border [&_td]:border-zinc-200 [&_td]:px-2 [&_td]:py-1 dark:[&_td]:border-zinc-700',
        '[&_blockquote]:border-l-2 [&_blockquote]:border-zinc-300 [&_blockquote]:pl-3 [&_blockquote]:text-zinc-600 dark:[&_blockquote]:border-zinc-600 dark:[&_blockquote]:text-zinc-400',
        className
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
        components={{
          pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
          a: ({ children, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {children as ReactNode}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});
